<template>
  <a-table :columns="columns" :data-source="dataSource" :pagination="false">
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'themeId'">
        {{ getThemeName(record.themeId) }}
      </template>
      <template v-else-if="column.key === 'colorId'">
        {{ colorName(record['themeId'], record['colorId']) }}
      </template>

      <template v-else-if="column.key === 'shapeId'">
        {{ getShapeName(record['shapeId']) }}
      </template>
      <template v-else-if="column.key === 'rule'">
        <template v-if="record['tooltip'] && record['rule'].includes(': ')">
          <span>{{ record['rule'].split(': ')[0] }}</span>
          <a-tooltip :title="record['tooltip']">
            <icon class="ml-0.5 mr-0.5 !cursor-default inline-block align-middle" icon="help.svg" :width="14" :height="14" fill="none" />
          </a-tooltip>
          <span>: {{ record['rule'].split(': ').slice(1).join(': ') }}</span>
        </template>
        <template v-else>
          {{ record['rule'] }}
        </template>
      </template>
      <template v-else-if="column.key === 'valid'">
        <span v-if="!record['valid']" class="text-red-500"> 未通过</span>
        <span v-else class="text-green-400">通过</span>
      </template>
      <template v-else-if="column.key === 'action'">
        <a-button size="small" @click="() => handleLayerClick(record)"
          >查看</a-button
        >
      </template>
    </template>
  </a-table>
</template>

<script setup lang="ts">
import { AodThemeValidRse } from '@/hooks';
import { Shape } from '@/services/shape';
import { useEditorStore } from '@/store/use-editor-store';
import { useLayerStore } from '@/store/use-layer-store';
import { round } from 'lodash-es';
import { computed, PropType } from 'vue';

const props = defineProps({
  data: {
    type: Array as PropType<AodThemeValidRse[]>,
    default: () => []
  }
});

const layerStore = useLayerStore();
const editorStore = useEditorStore();

const columns = [
  {
    title: '所属样式',
    key: 'themeId'
  },
  {
    title: '配色',
    key: 'colorId'
  },
  {
    title: '图层',
    key: 'shapeId'
  },
  {
    title: '校验规则',
    key: 'rule'
  },
  {
    title: '是否通过',
    key: 'valid'
  },
  { title: '操作', key: 'action' }
];

const dataSource = computed(() => {
  const res: {
    themeId: string;
    colorId: string;
    shapeId?: string;
    rule: string;
    tooltip?: string;
    valid: boolean;
  }[] = [];

  props.data.forEach((item) => {
    const { themeId, colorId, nonBlack, illegalColor, RGB } = item;
    if (nonBlack) {
      const ratio = round(nonBlack.ratio * 100, 2);
      res.push({
        themeId,
        colorId,
        rule: `息屏非黑像素占比不可超过${nonBlack.max * 100}%：当前${ratio}%;`,
        valid: nonBlack.valid
      });
    }
    if (illegalColor) {
      const ratio = round(illegalColor.ratio * 100, 2);
      res.push({
        themeId,
        colorId,
        rule: `息屏不合法颜色（max(R,G)/2>=B）像素占比不可超过20%：当前${ratio}%;`,
        valid: illegalColor.valid
      });
    }
    if (RGB?.movable) {
      const desc = RGB.movable.desc;
      const tooltip = RGB.movable.tooltip;
      RGB.movable.invalidShapes.forEach((item) => {
        const { shapeId, valid } = item;
        res.push({
          themeId,
          colorId,
          shapeId,
          rule: `${desc}`,
          tooltip,
          valid
        });
      });
    }
    // 自动生成其他的
    if (RGB?.partiallyMovable) {
      const desc = RGB.partiallyMovable.desc;
      const tooltip = RGB.partiallyMovable.tooltip;
      RGB.partiallyMovable.invalidShapes.forEach((item) => {
        const { shapeId, valid } = item;
        res.push({
          themeId,
          colorId,
          shapeId,
          rule: `${desc}`,
          tooltip,
          valid
        });
      });
    }
    if (RGB?.notMovable) {
      const desc = RGB.notMovable.desc;
      const tooltip = RGB.notMovable.tooltip;
      RGB.notMovable.invalidShapes.forEach((item) => {
        const { shapeId, valid } = item;
        res.push({
          themeId,
          colorId,
          shapeId,
          rule: `${desc}`,
          tooltip,
          valid
        });
      });
    }
  });

  return res;
});

const emit = defineEmits<{
  (event: 'close'): void;
}>();

const handleLayerClick = (i: {
  themeId: string;
  colorId: string;
  shapeId?: string;
}) => {
  editorStore.selectTheme(i.themeId);
  i.colorId && editorStore.selectThemeColor(i.colorId);
  i.shapeId && editorStore.select([i.shapeId]);
  emit('close');
};

const getShapeName = (shapeId: string) => {
  const shape = Shape.getShape(shapeId);
  if (!shape) return '-';
  return shape.attrs.name;
};

const colorName = (themeId: string, colorId: string) =>
  layerStore.themes
    .find((i) => i.id === themeId)
    ?.colorGroupTable.find((i) => i.id === colorId)?.name ?? '-';

const getThemeName = (themeId: string) => {
  const theme = layerStore.findTheme(themeId);
  if (!theme) return '-';
  if (theme.type == 'normal') return theme.name;

  // 查找对应的亮屏
  const normalTheme = layerStore.themes.find(
    (item) => item.name == theme.name && item.type == 'normal'
  );
  if (!normalTheme) return '-';
  if (layerStore.themes.filter((item) => item.type == 'AOD').length < 2)
    return '息屏';
  return normalTheme.name + '的息屏';
};
</script>
<style lang="less"></style>
